import React, { useState, useEffect, useCallback } from 'react';
import { css } from '@emotion/react';

interface CreateAiBgmModalTemplateProps {
  bgmDescription?: string;
  onDescriptionChange?: (description: string) => void;
}

const MAX_DESCRIPTION_LENGTH = 200;

function CreateAiBgmModalTemplate({
  bgmDescription: externalBgmDescription,
  onDescriptionChange
}: CreateAiBgmModalTemplateProps) {
  const [internalBgmDescription, setInternalBgmDescription] = useState<string>('');

  // 외부 props와 내부 상태 동기화
  useEffect(() => {
    if (externalBgmDescription !== undefined) {
      setInternalBgmDescription(externalBgmDescription);
    }
  }, [externalBgmDescription]);

  // 현재 사용할 값 결정
  const currentBgmDescription = externalBgmDescription ?? internalBgmDescription;

  // 설명 변경 핸들러
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;

      // MAX_DESCRIPTION_LENGTH를 초과하는 입력 방지
      if (value.length > MAX_DESCRIPTION_LENGTH) {
        return;
      }

      if (externalBgmDescription !== undefined) {
        onDescriptionChange?.(value);
      } else {
        setInternalBgmDescription(value);
        onDescriptionChange?.(value);
      }
    },
    [externalBgmDescription, onDescriptionChange]
  );

  return (
    <div css={styles.container}>
      <div css={styles.textAreaContainer}>
        <textarea
          css={styles.textArea}
          value={currentBgmDescription}
          onChange={handleDescriptionChange}
          placeholder="스토리의 분위기와 상황을 간단히 적어주세요. (예: 긴장되는 추격전, 잔잔한 숲속 모험)"
          maxLength={MAX_DESCRIPTION_LENGTH}
        />
        <div css={styles.characterCount}>
          {currentBgmDescription.length}/{MAX_DESCRIPTION_LENGTH}
        </div>
      </div>
    </div>
  );
}

export default CreateAiBgmModalTemplate;

// 스타일 정의
const styles = {
  container: css`
    width: 100%;
    height: 100%;
  `,

  textAreaContainer: css`
    position: relative;
    width: 100%;
    background-color: #fafafa;
    border: 1px solid #ededed;
    border-radius: 4px;
    overflow: hidden;
  `,

  textArea: css`
    width: 100%;
    min-height: 80px;
    padding: 12px 12px 28px 12px;
    border: none;
    background: transparent;
    font-family: 'Pretendard', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #0e1017;
    resize: vertical;
    outline: none;
    box-sizing: border-box;

    &::placeholder {
      color: #b2b6c2;
    }

    &:focus {
      outline: none;
    }
  `,

  characterCount: css`
    position: absolute;
    bottom: 8px;
    right: 12px;
    font-family: 'Pretendard', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #626262;
    background-color: #fafafa;
    pointer-events: none;
  `
};
